'use client'

import { useEffect } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { setCookie, getCookie } from 'cookies-next'
import { detectInAppBrowser } from '@/lib/detectInAppBrowser'
import { safeSessionGet, safeSessionSet } from '@/lib/analytics/sessionStore'
import {
    buildCampaignTouchPayload,
    computeCampaignTouchFingerprint,
    postCampaignTouch,
    resolveSegmentAnonymousId,
} from '@/lib/attribution/campaignTouch'

export const ATTRIBUTION_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'msclkid',
    'kad_cid',
    'oppref',
] as const

export const IN_APP_BROWSER_COOKIE = 'in_app_browser'
export const FBP_COOKIE = '_fbp'
export const FBC_COOKIE = '_fbc'

const COOKIE_MAX_AGE_SECONDS = 604800 // 7 days — aligned with Meta's 7-day click attribution window

// sessionStorage key prefix guarding against re-posting an identical CampaignTouch
// during in-tab navigations within the same browser session.
const CAMPAIGN_TOUCH_SESSION_GUARD_PREFIX = 'utm_touch_synced:'

// Synchronous per-page-load guard shared across every mounted instance of the
// hook. The store layout mounts the tracker in both the root layout and the
// home page, so two instances run on the same tick; this Set stops the second
// from firing a duplicate touch before the sessionStorage guard is written.
const attemptedCampaignTouchFingerprints = new Set<string>()

/**
 * Fires a CampaignTouch on a UTM-bearing landing: when the URL carries a
 * utm_source, a Segment anonymous id is resolvable, and this UTM fingerprint
 * has not already been posted in this tab session.
 */
function maybePostCampaignTouch(searchParams: URLSearchParams) {
    if (!searchParams.get('utm_source')) {
        return
    }

    // Check the set and session storage to confirm if the fingerprint has already
    // been posted.
    const fingerprint = computeCampaignTouchFingerprint(searchParams)
    if (attemptedCampaignTouchFingerprints.has(fingerprint)) {
        return
    }

    const guardKey = `${CAMPAIGN_TOUCH_SESSION_GUARD_PREFIX}${fingerprint}`
    if (safeSessionGet(guardKey)) {
        return
    }

    attemptedCampaignTouchFingerprints.add(fingerprint)

    const inAppBrowser = String(getCookie(IN_APP_BROWSER_COOKIE) || '') || detectInAppBrowser() || undefined

    resolveSegmentAnonymousId((anonymousId) => {
        const payload = buildCampaignTouchPayload({
            searchParams,
            anonymousId,
            landingPath: window.location.pathname,
            inAppBrowser,
            fbc: getCookie(FBC_COOKIE)?.toString(),
            fbp: getCookie(FBP_COOKIE)?.toString(),
        })

        if (!payload) {
            return
        }

        void postCampaignTouch(payload).then((recorded) => {
            if (recorded) {
                // Persist the tab-session guard only once the server confirms
                // the touch, so a failed or 502 response can be retried on a
                // later navigation carrying the same UTM fingerprint.
                safeSessionSet(guardKey, '1')
            } else {
                // Release the per-page-load guard so the same tab can re-attempt
                // the touch that was never recorded server-side.
                attemptedCampaignTouchFingerprints.delete(fingerprint)
            }
        })
    })
}

function setCookieWithAttributionSettings(name: string, value: string) {
    setCookie(name, value, {
        path: '/',
        maxAge: COOKIE_MAX_AGE_SECONDS,
        sameSite: 'lax',
        domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
    })
}

function setFallbackFbpCookie() {
    if (getCookie(FBP_COOKIE)) {
        return
    }

    const fbp = `fb.1.${Date.now()}.${Math.floor(Math.random() * 1e10)}`
    setCookieWithAttributionSettings(FBP_COOKIE, fbp)
}

function setFbcCookie(fbclid: string) {
    const currentFbc = getCookie(FBC_COOKIE)?.toString()
    if (currentFbc?.endsWith(`.${fbclid}`)) {
        return
    }

    setCookieWithAttributionSettings(FBC_COOKIE, `fb.1.${Date.now()}.${fbclid}`)
}

export function useAttributionTracking() {
    const searchParams = useSearchParams()
    const pathname = usePathname() || ''

    useEffect(() => {
        ATTRIBUTION_PARAMS.forEach((param) => {
            const value = searchParams.get(param)
            if (value) {
                setCookie(param, value, {
                    path: '/',
                    maxAge: COOKIE_MAX_AGE_SECONDS,
                    sameSite: 'lax',
                    domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
                })
            }
        })

        const fbclid = searchParams.get('fbclid')
        if (fbclid) {
            setFbcCookie(fbclid)
        }

        // No client-side Meta Pixel remains to set `_fbp` on its own, so the fallback
        // cookie is the only source now — set it immediately rather than deferring,
        // since `maybePostCampaignTouch` below reads it in this same tick.
        setFallbackFbpCookie()

        if (!getCookie(IN_APP_BROWSER_COOKIE)) {
            const browser = detectInAppBrowser()
            if (browser) {
                setCookieWithAttributionSettings(IN_APP_BROWSER_COOKIE, browser)
            }
        }

        maybePostCampaignTouch(searchParams)
    }, [pathname, searchParams])
}

